// frontend/src/components/notifications/NotificationCenter.jsx
/**
 * NotificationCenter — full-page notification history with unified UX.
 *
 * @description
 *   Same Atlassian-style date sectioning + element grouping + expand-to-show-
 *   children behavior as the dropdown (NotificationDropdown). Adds page-level
 *   filters: domain dropdown, status segment (All/Unread/Archive), search.
 *   Mobile gets always-visible action icons (≥44px touch targets) plus
 *   swipe-left=mute / swipe-right=archive on group rows.
 *
 * @useWhen
 *   - Mounted at /notifications route
 *
 * @related
 *   - NotificationDropdown — the bell-attached compact view (same grouping)
 *   - groupNotifications.js — shared grouping helper
 *   - domainIcon.js — shared per-domain icon + urgency colors
 *   - hooks/useSwipeRow.js — pointer-event swipe gesture hook
 */
import { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import {
  Box, Stack, Typography, IconButton, Chip,
  Tooltip, Collapse, Alert, Skeleton,
} from '@mui/material'
// 🔴 G14 (UI-Durchgang 2 Teil 5 / M2): hier stand `DeleteIcon` — ein
// MUeLLEIMER an einer Handlung, die ARCHIVIERT. Die Handlung war nie falsch
// (`onDelete` ist `deleteRow`, und das ruft `softDelete` mit einem
// `unarchive`-Rueckweg); falsch war allein das Symbol. Es gibt DREI Stellen,
// die es trugen (rechter Zeilenknopf, Wisch-Hinweis, `GroupRow`-Aktionsfeld) —
// alle drei tragen jetzt das Archiv-Symbol.
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import VolumeOffIcon from '@mui/icons-material/VolumeOff'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useNavigate } from 'react-router-dom'
import PageContainer from '../common/PageContainer'
import ViewHeader from '../ViewHeader'
import SearchTrigger from '../common/SearchTrigger'
import BottomSheetSelect from '../common/BottomSheetSelect'
import UnderlineTabs from '../common/UnderlineTabs'
import { useNotificationsContext } from '../../notifications/NotificationsContext'
import { groupAndSection } from '../../notifications/groupNotifications'
import { getDomainIcon, getUrgencyColor } from '../../notifications/domainIcon'
import { useSwipeRow } from '../../hooks/useSwipeRow'
import { FS, SP, PANEL_SX, RAIL_WIDTH, SWIPE_UNDO_MS, TOAST_DURATION, hoverOnly, focusRing } from '../../theme/tokens'
import { v2Tone } from '../../theme/theme'
import { toast } from 'sonner'

const API = process.env.REACT_APP_API_BASE_URL

const DOMAIN_OPTIONS = [
  { value: '', label: 'All domains' },
  { value: 'rma', label: 'RMA' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'sync', label: 'Sync' },
  { value: 'ops', label: 'Ops' },
  { value: 'po', label: 'PO' },
  { value: 'scraper', label: 'Scraper' },
]

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'archive', label: 'Archive' },
]

// E9 (2026-10-07): hier stand `TOUCH_TARGET_SX = { minWidth: 44, minHeight: 44 }`,
// von Hand an sechs `IconButton` gehaengt, sobald `isMobile` galt. Das war die
// ZWEITE Quelle desselben Bodens: `isMobile` war ein
// `useMediaQuery(theme.breakpoints.down('sm'))`, und `theme.js` setzt
// fuer `MuiIconButton` genau unter `down('sm')` `minWidth/minHeight: 44`.
// Der Theme-Boden traegt jetzt allein — die JS-Kopie ist gefallen.
// 🔴 Nachtrag 2026-10-07 (F6, UI-Durchgang 2): der `isMobile`-Schalter SELBST ist
// mit dieser Runde gefallen — er trug nur noch die Statuszeile (Pillen mobil,
// `SegmentedSlider` Desktop), und die ist jetzt auf BEIDEN Breiten
// `UnderlineTabs`. Der Satz oben bleibt als Aufzeichnung stehen; sein Zeiger auf
// die alte Zeile hat kein Ziel mehr, weil es die Zeile nicht mehr gibt.

function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 48) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function elementSubtitle(group_key) {
  const parts = group_key.split(':')
  if (parts[0] === 'rma' && parts[1] === 'case') return `RMA Case ${parts[2]}`
  if (parts[0] === 'po') return `PO ${parts[1]}`
  if (parts[0] === 'pricing') return `Pricing · ${parts[1]}`
  if (parts[0] === 'scraper') return `Scraper · ${parts[3] || parts[2]}`
  if (parts[0] === 'ops') return `Ops · ${parts[1].split('/').pop()}`
  if (parts[0] === 'lsm') return `LSM watch ${parts[1]}`
  return ''
}

function ChildRow({ notification, onNavigate, onMute, onDelete }) {
  const isUnread = notification.seen_at == null
  const urgencyColor = getUrgencyColor(notification.urgency)
  return (
    <Box
      onClick={() => onNavigate(notification)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNavigate(notification) } }}
      sx={{
        position: 'relative',
        pl: SP.section + SP.card, pr: SP.section, py: 1,
        cursor: 'pointer',
        bgcolor: isUnread ? 'action.hover' : 'transparent',
        borderBottom: '1px solid', borderBottomColor: 'divider',
        borderLeft: `${RAIL_WIDTH}px solid`, borderLeftColor: urgencyColor,
        // 🔴 R16 C2: die EINBLENDUNG gehoert nicht hinter `hoverOnly` — sie ist keine
        // Hover-Wirkung, sondern eine Ruhelage, die an der ZEIGERFAEHIGKEIT haengt und
        // nicht an der Breite. `opacity: { xs: 1, sm: 0 }` nimmt die Aktionen ab 600 px
        // weg; auf einem 1024-px-Tablet (`(hover: none)`) holt sie kein Hover zurueck.
        // Hausform `RMACasesDashboardBody.jsx` › `.caseHoverActions` (W-52).
        '&:hover .child-actions': { opacity: 1 },
        '@media (hover: none)': { '& .child-actions': { opacity: 1 } },
        ...hoverOnly({ bgcolor: 'action.selected' }),
        // Innen versetzt: die Zeile ist dicht, ein Aussenring schnitte an der
        // Nachbarzeile an (FOCUS_RING.innen).
        '&:focus-visible': focusRing({ innen: true }),
        '&:focus-within .child-actions': { opacity: 1 },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography sx={{
              fontSize: FS.sm, fontWeight: isUnread ? 600 : 400,
              color: 'text.primary', flexGrow: 1,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {notification.title}
            </Typography>
            <Typography sx={{ fontSize: FS.xxs, color: 'text.disabled', flexShrink: 0 }}>
              {timeAgo(notification.created_at)}
            </Typography>
          </Stack>
          {notification.body && (
            <Typography sx={{
              fontSize: FS.xs, color: 'text.secondary', mt: 0.25,
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>
              {notification.body}
            </Typography>
          )}
        </Box>
        <Stack
          direction="row"
          spacing={0.25}
          className="child-actions"
          sx={{
            opacity: { xs: 1, sm: 0 },
            transition: 'opacity 0.15s',
            flexShrink: 0,
          }}
        >
          <Tooltip title="Mute for 24h">
            <IconButton
              size="small"
              aria-label="Mute for 24h"
              onClick={(e) => { e.stopPropagation(); onMute(notification) }}
            >
              <VolumeOffIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Archive">
            <IconButton
              size="small"
              aria-label="Archive notification"
              onClick={(e) => { e.stopPropagation(); onDelete(notification) }}
            >
              <ArchiveOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
        {isUnread && (
          <Box sx={{
            width: 6, height: 6, borderRadius: '50%',
            bgcolor: urgencyColor, alignSelf: 'center', flexShrink: 0,
          }} />
        )}
      </Stack>
    </Box>
  )
}

function GroupRow({ group, onNavigate, onMute, onDelete }) {
  const { latest, items, has_unread } = group
  const isMulti = items.length > 1
  const [expanded, setExpanded] = useState(false)

  const DomainIcon = getDomainIcon(latest.domain)
  const urgencyColor = getUrgencyColor(latest.urgency)
  const subtitle = elementSubtitle(group.group_key)
  const moreCount = items.length - 1

  const { handlers: swipeHandlers, dx, swiping } = useSwipeRow({
    onSwipeLeft: () => onMute(latest),
    onSwipeRight: () => onDelete(latest),
  })

  const handleClick = () => {
    if (swiping) return
    if (isMulti) setExpanded(e => !e)
    else onNavigate(latest)
  }

  // Visual hints under the row while swiping.
  // 🔴 G9: das ETIKETT (`swipeHintLabel`) bleibt, wie es ist, und die FARBE
  // kommt aus der v2-Skala statt aus einem Literal. Geaendert hat sich allein
  // das SYMBOL des Rechts-Wischs — G14, weil die Handlung dahinter archiviert.
  //
  // 🔴 K-2 (UI2 Teil 8, 2026-10-10) — die Flaeche ist GEFUELLT, aber aus der
  // v2-Skala: `v2Tone(theme, ton).main` als GRUND, modusabhaengige Schrift
  // darauf. Sie trug `palette.<ton>.main` + `<ton>.contrastText`; im HELLEN
  // Modus liefert weisse Schrift auf `warning.main` **3,113:1** — unter 4,5
  // fuer 11,5-px-Text (`FS.xs`, WCAG 1.4.3). Der dunkle Modus trug (9,15:1), der Defekt
  // war ein HELLER. Warum nicht `soft` als Flaeche: die soft-Familie erreicht
  // als FLAECHE keine 3:1 gegen die Seite (hell 1,009:1). Diese Paarung traegt
  // BEIDES — Schrift >= 4,5:1 UND Flaeche >= 3:1 gegen die Seite — in beiden
  // Modi und beiden Toenen; gemessen in `NotificationCenter.wischhinweis.test.jsx`
  // (beide Toene, beide Modi, samt Kontrollrechnung auf dem alten Paar).
  // Hausform: `POInbox.jsx` › `wischFlaeche`, `rma/RMAInbox.jsx`.
  const swipeHintTone = dx < 0 ? 'warn' : 'err'
  const swipeHintIcon = dx < 0 ? <VolumeOffIcon sx={{ fontSize: 20 }} /> : <ArchiveOutlinedIcon sx={{ fontSize: 20 }} />
  const swipeHintLabel = dx < 0 ? 'Mute' : 'Archive'

  return (
    <>
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        {Math.abs(dx) > 8 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={dx < 0 ? 'flex-end' : 'flex-start'}
            spacing={0.5}
            data-testid="swipe-row-hint"
            sx={(t) => ({
              position: 'absolute', inset: 0,
              px: SP.section,
              bgcolor: v2Tone(t, swipeHintTone).main,
              // Nie `common.white` im Dunkelmodus: auf dem dunklen Warn-Ton
              // traegt es 1,67:1. Die Schrift folgt deshalb dem Modus.
              color: t.palette.mode === 'dark' ? t.palette.background.default : t.palette.common.white,
              fontSize: FS.xs, fontWeight: 600,
            })}
          >
            {swipeHintIcon}
            <span>{swipeHintLabel}</span>
          </Stack>
        )}
        <Box
          {...swipeHandlers}
          onClick={handleClick}
          role="button"
          tabIndex={0}
          aria-expanded={isMulti ? expanded : undefined}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick() } }}
          data-testid="swipe-row-foreground"
          sx={{
            position: 'relative',
            px: SP.section, py: 1.25,
            cursor: 'pointer',
            // 🔴 G10 — die Flaeche, die sich verschiebt, ist DECKEND. Sie stand
            // hier auf `action.hover`, einem durchscheinenden MUI-Ton: das
            // farbige Aktionsfeld darunter schien durch den verschobenen
            // Vordergrund (Fotobefund G5-2, dunkler Text auf Orange).
            bgcolor: 'background.paper',
            borderBottom: '1px solid', borderBottomColor: 'divider',
            transform: `translateX(${dx}px)`,
            transition: swiping ? 'none' : 'transform 0.18s ease',
            // 🔴 G8 — diese CSS-Transition sieht `MotionConfig` NICHT (das deckt
            // nur framer-motion ab, `App.js:213`). Wer dem System gesagt hat,
            // dass er weniger Bewegung will, bekommt hier keine.
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            // 🔴 R16 C2: siehe ChildRow oben — die Einblendung ist eine Ruhelage an der
            // Zeigerfaehigkeit, keine Hover-Wirkung. Hausform `RMACasesDashboardBody.jsx` › `.caseHoverActions`.
            '&:hover .row-actions': { opacity: 1 },
            '@media (hover: none)': { '& .row-actions': { opacity: 1 } },
            ...hoverOnly({ '& .row-tint': { bgcolor: 'action.selected' } }),
            // Innen versetzt: die Zeile ist dicht, ein Aussenring schnitte an der
            // Nachbarzeile an (FOCUS_RING.innen).
            '&:focus-visible': focusRing({ innen: true }),
            '&:focus-within .row-actions': { opacity: 1 },
            ...swipeHandlers.style,
          }}
        >
          {/* 🔴 G10 — die Toenung („ungelesen") gehoert auf eine INNERE Flaeche
              ueber der deckenden Grundfarbe, nicht auf den Vordergrund selbst.
              Hausform: `RMACasesDashboardBody.jsx` › `CaseCard` (der deckende Ruecken auf
              dem `motion.div`) plus die Toenung auf dem inneren `Paper`. */}
          <Box
            className="row-tint"
            sx={{
              position: 'absolute', inset: 0,
              bgcolor: has_unread ? 'action.hover' : 'transparent',
              pointerEvents: 'none',
            }}
          />
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ position: 'relative' }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '50%',
              bgcolor: 'background.default', border: '1px solid', borderColor: 'divider',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <DomainIcon sx={{ fontSize: 20, color: urgencyColor }} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
                <Typography sx={{
                  fontSize: FS.md, fontWeight: has_unread ? 700 : 500,
                  color: 'text.primary', flexGrow: 1,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {latest.title}
                </Typography>
                <Typography sx={{ fontSize: FS.xs, color: 'text.disabled', flexShrink: 0 }}>
                  {timeAgo(latest.created_at)}
                </Typography>
              </Stack>
              {subtitle && (
                <Typography sx={{ fontSize: FS.sm, color: 'text.secondary', mb: 0.25 }}>
                  {subtitle}
                </Typography>
              )}
              {latest.body && (
                <Typography sx={{
                  fontSize: FS.sm, color: 'text.secondary', mb: 0.5,
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>
                  {latest.body}
                </Typography>
              )}
              {isMulti && (
                <Chip
                  size="small"
                  label={`${expanded ? 'Hide' : 'Show'} ${moreCount} more update${moreCount === 1 ? '' : 's'}`}
                  icon={expanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                  onClick={(e) => { e.stopPropagation(); setExpanded(x => !x) }}
                  sx={{
                    fontSize: FS.xxs, height: 22,
                    bgcolor: expanded ? 'primary.50' : 'action.selected',
                    color: 'primary.main',
                    fontWeight: 600,
                    cursor: 'pointer',
                    '& .MuiChip-icon': { ml: 0.5, color: 'primary.main' },
                    ...hoverOnly({ bgcolor: 'primary.100' }),
                  }}
                />
              )}
            </Box>
            <Stack
              direction="row"
              spacing={0.25}
              className="row-actions"
              sx={{
                opacity: { xs: 1, sm: 0 },
                transition: 'opacity 0.15s',
                flexShrink: 0,
              }}
            >
              <Tooltip title="Mute for 24h">
                <IconButton
                  size="small"
                  aria-label="Mute for 24h"
                  onClick={(e) => { e.stopPropagation(); onMute(latest) }}
                >
                  <VolumeOffIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Archive">
                <IconButton
                  size="small"
                  aria-label="Archive notification"
                  onClick={(e) => { e.stopPropagation(); onDelete(latest) }}
                >
                  <ArchiveOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
            {isMulti && (
              <Tooltip title={expanded ? 'Collapse updates' : `Show ${moreCount} more`}>
                <IconButton
                  size="small"
                  aria-label={expanded ? 'Collapse updates' : `Show ${moreCount} more updates`}
                  aria-expanded={expanded}
                  onClick={(e) => { e.stopPropagation(); setExpanded(x => !x) }}
                  sx={{
                    flexShrink: 0,
                    transform: expanded ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.18s ease',
                  }}
                >
                  <ExpandMoreIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>
            )}
            {has_unread && (
              <Box sx={{
                width: 10, height: 10, borderRadius: '50%',
                bgcolor: urgencyColor, alignSelf: 'center', flexShrink: 0,
              }} />
            )}
          </Stack>
        </Box>
      </Box>
      {isMulti && (
        <Collapse in={expanded} timeout={220} unmountOnExit>
          <Box sx={{
            bgcolor: 'background.default',
            ml: SP.section,
            borderLeft: '2px solid',
            borderLeftColor: 'primary.light',
          }}>
            {items.map(child => (
              <ChildRow
                key={child.id}
                notification={child}
                onNavigate={onNavigate}
                onMute={onMute}
                onDelete={onDelete}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </>
  )
}

export default function NotificationCenter() {
  const navigate = useNavigate()
  const { unreadCount, markSeen, markAllSeen, softDelete } = useNotificationsContext()
  const [items, setItems] = useState([])
  const [domain, setDomain] = useState('')
  const [status, setStatus] = useState('all') // all | unread | archive
  const [muteCount, setMuteCount] = useState(0)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(null)

  const onlyUnread = status === 'unread'
  const showArchive = status === 'archive'

  useEffect(() => {
    const params = { limit: 200 }
    if (domain) params.domain = domain
    if (showArchive) params.older_than = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
    setLoading(true)
    axios.get(`${API}/api/notify/notifications`, { params })
      .then(r => { setItems(r.data.notifications || []); setFetchError(null) })
      .catch(e => { setItems([]); setFetchError(e?.response?.data?.error || e.message || 'Failed to load notifications') })
      .finally(() => setLoading(false))
  }, [domain, showArchive])

  useEffect(() => {
    axios.get(`${API}/api/notify/mutes`)
      .then(r => setMuteCount((r.data.mutes || []).length))
      .catch(() => setMuteCount(0))
  }, [items])

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items
    const q = search.toLowerCase()
    return items.filter(n =>
      (n.title || '').toLowerCase().includes(q)
      || (n.body || '').toLowerCase().includes(q)
      || (n.trigger_key || '').toLowerCase().includes(q)
    )
  }, [items, search])

  const sections = useMemo(
    () => groupAndSection(filteredItems, { onlyUnread }),
    [filteredItems, onlyUnread]
  )

  const navigateToNotification = (notification) => {
    if (notification.seen_at == null) markSeen(notification.id)
    if (notification.deep_link) navigate(notification.deep_link)
  }

  const muteRow = async (n) => {
    const muteBody = n.dedup_key
      ? { dedup_key: n.dedup_key, duration_hours: 24, reason: 'muted from notification center' }
      : { trigger_key: n.trigger_key, duration_hours: 24, reason: 'muted from notification center' }
    const removed = items.find(x => x.id === n.id)
    setItems(items.filter(x => x.id !== n.id))
    try {
      const res = await axios.post(`${API}/api/notify/mutes`, muteBody)
      const muteId = res.data?.mute?.id || res.data?.id
      // 🔴 G7 — die Ruecknahme steht im sonner-Toast (Hausform
      // `products/SparePartsSection.jsx:212-229`), nicht mehr in einer eigenen
      // MUI-`Snackbar` am Seitenende. Der Grund ist die Frist: ein Wisch loest
      // leichter versehentlich aus, und `SWIPE_UNDO_MS` gilt fuer beide Wege.
      toast('Muted for 24h', {
        duration: SWIPE_UNDO_MS,
        action: {
          label: 'Undo',
          onClick: async () => {
            if (muteId) {
              try { await axios.delete(`${API}/api/notify/mutes/${muteId}`) } catch (_) { /* best effort */ }
            }
            if (removed) setItems(prev => [removed, ...prev.filter(x => x.id !== removed.id)])
          },
        },
      })
    } catch (e) {
      if (removed) setItems(prev => [removed, ...prev.filter(x => x.id !== removed.id)])
      // 🔴 K-F3 (2026-10-09): der Fehlschlag steht im sonner-Toast, nicht mehr
      // in einer eigenen MUI-`Snackbar` am Seitenende — dieselbe Flaeche, auf
      // der die Ruecknahme daneben schon liegt. Die lange Dauer ist die des
      // Fehlers: er will gelesen werden.
      toast.error(`Mute failed: ${e?.response?.data?.error || e.message}`, {
        duration: TOAST_DURATION.long,
      })
    }
  }

  const deleteRow = async (n) => {
    const removed = items.find(x => x.id === n.id)
    setItems(items.filter(x => x.id !== n.id))
    try {
      await softDelete(n.id)
      // G7 — dieselbe Ruecknahme wie oben; die Gegenrichtung ist
      // `POST /api/notify/notifications/<id>/unarchive`.
      toast('Archived', {
        duration: SWIPE_UNDO_MS,
        action: {
          label: 'Undo',
          onClick: async () => {
            try { await axios.post(`${API}/api/notify/notifications/${n.id}/unarchive`) } catch (_) { /* best effort */ }
            if (removed) setItems(prev => [removed, ...prev.filter(x => x.id !== removed.id)])
          },
        },
      })
    } catch (e) {
      if (removed) setItems(prev => [removed, ...prev.filter(x => x.id !== removed.id)])
      toast.error(`Archive failed: ${e?.response?.data?.error || e.message}`, {
        duration: TOAST_DURATION.long,
      })
    }
  }

  return (
    <PageContainer>
      {/* 🔴 F6 (UI-Durchgang 2, 2026-10-07): hier stand
          `breadcrumbs={[{ label: 'Notifications' }]}` — ein Kruem, der
          WOERTLICH den Titel wiederholte. Ein Brotkruem ist ein Rueckweg; ein
          Rueckweg auf dieselbe Seite ist keiner. Der Kopf trug damit zwei
          Ueberschriften derselben Sache uebereinander. */}
      {/* 🔴 K-11 (UI-Durchgang 2 Teil 6, 2026-10-10): „Mark all as read" ist eine
          SEITENWEITE Handlung und stand in der Werkzeugzeile der Liste — sie
          gehoert in den Kopf (`actions`). Das Kopfaktionsmodell sagt: Knöpfe
          gehoeren in `actions`/`overflow`, die Zeile darunter traegt Suche,
          Filter und Zeilenwerkzeuge.
          🔴 Sie traegt `variant: 'outlined'` und ist damit die SEKUNDAERE Form,
          nicht die gefuellte: der Kopf rendert seine einzige Aktion ohne
          ausdrueckliches `variant` als `contained`, und das waere hier falsch.
          Die Aufgabe dieser Seite ist LESEN; „alles gelesen" ist eine
          Bequemlichkeit am Rand, keine Handlung, die die Seite veranlasst
          (Gegenbeispiel: „Backfill RT watches" in `ops/LsmWatcher.jsx`, dort
          `emphasis: 'primary'`). Die Probe:
          `frontend/tests/design/ui2-k-11-kopfaktionen.spec.ts`.
          🔴 W1 der Regel-16-Wiedervorlage 1 (2026-10-10): der Eintrag ist
          GESPERRT statt ENTFERNT, wenn nichts ungelesen ist — er bleibt stehen.
          Bis hierher hing er an `unreadCount > 0`, und der Wert startet bei 0
          (`notifications/useNotifications.js`); er trifft erst nach dem Abruf
          ein. `ViewHeader` legt Aktionen unter `sm` in eine EIGENE Zeile
          (`reserveActionsRow`), der Kopf wuchs also nachtraeglich um eine Zeile
          und schob die Liste — und im Fall „Zahl kommt als 0" waere er um
          dieselbe Zeile wieder GESCHRUMPFT, nur andersherum. Beides ist ein
          Sprung, und beide Male ist es derselbe Knopf: er ist die Zeile.
          🔴 Deshalb steht hier NICHT `actionsPending`. Die Prop reserviert die
          Zeile nur, solange `visible.length === 0` ist — sie deckt den Fall
          „Aktion fehlt noch" ab. Hier ist die Aktion nie weg, sie ist nur
          gesperrt: ein reservierter Platzhalter waere entweder wirkungslos
          (Aktion ist da) oder liesse im Fall „0 ungelesen" eine leere Zeile
          stehen.
          🔴 Und das ist kein Rueckbau des Leerzustands: `disabled` ist die
          Aussage „es gibt nichts zu markieren", sichtbar statt spurlos.
          Gemessen wird beides — die Hoehe bei 390 px vor und nach dem
          Eintreffen der Zahl
          (`frontend/tests/design/ui2-k-11-kopfaktionen.spec.ts`, verzoegerte
          Attrappe, beide Faelle) und der gesperrte Knopf samt fehlendem
          Platzhalter (`NotificationCenter.kopf.test.jsx`). */}
      <ViewHeader
        title="Notifications"
        actions={[
          {
            key: 'mark-all-read',
            label: 'Mark all as read',
            icon: <DoneAllIcon />,
            variant: 'outlined',
            disabled: unreadCount === 0,
            onClick: markAllSeen,
          },
        ]}
      />

      {fetchError && (
        <Alert severity="error" sx={{ mb: SP.section }} onClose={() => setFetchError(null)}>
          {fetchError}
        </Alert>
      )}

      <Box sx={{ ...PANEL_SX, p: SP.section, mb: SP.section }}>
        {/* Row 1 — utility row: search + domain + global actions */}
        <Stack
          direction="row"
          spacing={SP.sm}
          alignItems="center"
          sx={{ mb: SP.tight }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <SearchTrigger value={search} onChange={setSearch} placeholder="Search notifications…" />
          </Box>
          <BottomSheetSelect
            label="Domain"
            value={domain}
            onChange={setDomain}
            options={DOMAIN_OPTIONS}
            sx={{ minWidth: { xs: 'auto', sm: 160 } }}
          />
        </Stack>

        {/* Row 2 — Status segment. 🔴 F6 (UI-Durchgang 2, 2026-10-07): hier
            standen ZWEI Bedienelemente fuer dieselbe Sache — `FilterPillRow`
            unter `sm` und ein `SegmentedSlider` in einem 360-px-Kasten
            darueber. Der Fotobefund nannte die Desktop-Fassung „vierter
            Segment-/Tab-Stil" neben den drei Reiterformen der App. `All /
            Unread / Archive` ist genau der Fall, fuer den `UnderlineTabs` da
            ist („eine Liste nach Zustand segmentieren"); beide Breiten tragen
            jetzt denselben Reiter — und die Probe kann `role="tablist"`
            pruefen. */}
        <UnderlineTabs
          tabs={STATUS_OPTIONS.map((o) => o.label)}
          value={Math.max(0, STATUS_OPTIONS.findIndex((o) => o.value === status))}
          onChange={(i) => setStatus(STATUS_OPTIONS[i].value)}
        />

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: SP.tight }}>
          <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
            {filteredItems.length} of {items.length} shown · {unreadCount} unread overall
          </Typography>
          {muteCount > 0 && (
            <Chip
              size="small" color="default" variant="outlined"
              label={`${muteCount} active mute${muteCount === 1 ? '' : 's'}`}
              onClick={() => navigate('/settings/notifications')}
            />
          )}
        </Stack>
      </Box>

      <Box sx={PANEL_SX}>
        {loading ? (
          <Stack spacing={0.5} sx={{ p: SP.section }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={64} />
            ))}
          </Stack>
        ) : sections.length === 0 ? (
          <Typography sx={{ p: SP.section, color: 'text.secondary', textAlign: 'center' }}>
            {onlyUnread ? 'No unread notifications.' : (search ? 'No matches.' : 'All clear ✓')}
          </Typography>
        ) : (
          sections.map(section => (
            <Box key={section.date_label}>
              <Typography
                component="h2"
                sx={{
                  fontSize: FS.xs, fontWeight: 600, color: 'text.secondary',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  px: SP.section, py: 0.75,
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid', borderBottomColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  m: 0,
                }}
              >
                {section.date_label}
              </Typography>
              {section.groups.map(group => (
                <GroupRow
                  key={group.group_key}
                  group={group}
                  onNavigate={navigateToNotification}
                  onMute={muteRow}
                  onDelete={deleteRow}
                />
              ))}
            </Box>
          ))
        )}
      </Box>

    </PageContainer>
  )
}
